import { collectReportExtras, collectDatasources, parseIfString } from "./ReportLayoutUtils";

// Mirrors ReportLayoutUtils.js's private `normalizeDynamicValues` — that
// helper isn't exported, so it's duplicated here (same logic) purely so we
// can hand LayoutTablePrint a flat values map. collectReportExtras() does
// this same unwrapping internally for its own purposes, so this only needs
// to stay in sync if MakeReport.js's save shape ever changes.
const normalizeDynamicValues = (dynamicValues) => {
  if (
    dynamicValues &&
    typeof dynamicValues === "object" &&
    !Array.isArray(dynamicValues) &&
    dynamicValues.values &&
    typeof dynamicValues.values === "object"
  ) {
    return dynamicValues.values;
  }
  return dynamicValues;
};

// Add a fetcher here whenever a screen introduces a new datasource type that
// needs resolving for a *saved* report being reprinted (same idea as
// PatientReport.js's DATASOURCE_FETCHERS map). Left empty here since the
// public lab-report page doesn't have access to the internal per-datasource
// services (e.g. AntibioticService) that PatientReport.js's logged-in
// screen calls — wire in real fetchers if/when those become reachable from
// this context.
const DATASOURCE_FETCHERS = {
  // AntibioticByMainTest: async (mainTestId) => { ... return [{ value, label }, ...]; },
};

const resolveDatasources = async (rawLayout) => {
  const names = [...collectDatasources(rawLayout)];
  const entries = await Promise.all(
    names.map(async (name) => {
      const fetcher = DATASOURCE_FETCHERS[name];
      if (!fetcher) return [name, []];
      try {
        return [name, await fetcher()];
      } catch (e) {
        console.error(`Failed to resolve datasource "${name}" for print`, e);
        return [name, []];
      }
    })
  );
  return Object.fromEntries(entries);
};

export const buildPrintDataForReport = async (report) => {
  const rawLayout = report?.reportLayout;
  const rawDynamicValues = report?.dynamicValues;

  const layout = parseIfString(rawLayout);
  const dynamicValues = normalizeDynamicValues(parseIfString(rawDynamicValues));

  const showTable = layout?.showTestTable === "false" ? false : true;

  const dataSources = await resolveDatasources(rawLayout);
  const reportExtras = collectReportExtras(rawLayout, rawDynamicValues, dataSources);

  return {
    makeReportId: report?.makeReportId,
    mainTestId: report?.mainTestId,
    mainTestName: report?.mainTestName,
    testHeader: report?.testHeader,
    testGroupName: report?.testHeader || report?.mainTestName,
    result: report?.result,
    normalRange: report?.normalRange,
    impression: report?.impression,
    interpretation: report?.interpretation,
    method: report?.methodName,
    methodNameId: report?.methodNameId,
    patientComment: report?.patientComment,
    shortcutImpressionNote: report?.shortcutImpressionNote,
    subTests: report?.subTests || [],
    showTable,
    reportExtras,
    // Parsed object (not the raw/possibly-stringified value) so
    // LabReportPrintReport.js's `data?.reportLayout?.printLayoutAsTable`
    // check and its LayoutTablePrint render work without re-parsing.
    reportLayout: layout,
    dynamicValues,
  };
};

export const buildPrintDataList = async (reports) =>
  Promise.all((reports || []).map((report) => buildPrintDataForReport(report)));